import { AiBlockRequestSource, IntegrationAuthApp } from '@prisma/client';
import axios from 'axios';

import { AXIOS_CONSTANTS } from '@/levity/constants';
import { OperationInput } from '@/levity/flows/operation-center.types';

import { AppNodeType, AppSource, FullApp, NodeInputValues, Operation } from '../../../../flows/types';
import { NangoIntegrationProvider } from '../../../types';
import {
  ConectionConfigParamsType,
  CONNECTION_CONFIG_PARAMS,
  ERROR_MESSAGES,
  PARAMS,
  RESPONSE_CODES,
} from '../../constants';
import {
  DEFAULT_OPERATION_RETURN,
  getDefaultOperationReturn,
  returnOperationResult,
} from '../../operationRegistry-helpers';
import type {
  CustomerServiceProvider,
  GenericResponse,
  OperationResultResponse,
  Ticket,
} from '../customerServiceProvider';
import { zendeskOperationsDefinition } from './zendesk.definition';
import { ZendeskMembership, ZendeskTicketComment, ZendeskTicketStatus, ZendeskUser } from './zendesk.types';

const ZENDESK_API_URL = (domain: string) => `https://${domain}.zendesk.com/api/v2`;
const ZENDESK_TICKET_URL = (domain: string, ticketId: string) =>
  `https://${domain}.zendesk.com/agent/tickets/${ticketId}`;

const errorHandler = (error: Error): GenericResponse | OperationResultResponse => {
  let errorMessage = 'An unknown error occurred';
  if (error instanceof Error) {
    errorMessage = error.message;
  }
  return returnOperationResult({
    status: RESPONSE_CODES.ERROR,
    data: DEFAULT_OPERATION_RETURN,
    message: errorMessage,
  });
};

const getAuthenticatedZendeskInstance = (authToken: string, domain: string) => {
  return axios.create({
    baseURL: ZENDESK_API_URL(domain),
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    timeout: AXIOS_CONSTANTS.TIMEOUT,
  });
};

const getProfile = async (authToken: string, domain: string) => {
  const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
  try {
    const response = await zendeskApi.get('/users/me.json');
    return response.data.user;
  } catch (error) {
    errorHandler(error as Error);
  }
};

const connectionName = async (authToken: string, config?: ConectionConfigParamsType) => {
  const subdomainKey = CONNECTION_CONFIG_PARAMS.subdomain;

  const subdomain = config ? config[subdomainKey] : undefined;
  if (!subdomain) {
    throw new Error(ERROR_MESSAGES.SUBDOMAIN_NOT_FOUND.errorCode);
  }
  const profile = await getProfile(authToken, subdomain);
  if (!profile?.email) {
    throw new Error(ERROR_MESSAGES.CONNECTION_EMAIL_NOT_FOUND.description);
  }
  return profile?.email;
};

const updateTicketCategories = async (
  authToken: string,
  domain: string,
  [key, [inputValue]]: NodeInputValues,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    const { value: text } = inputValue!;
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.put(`/tickets/update_many.json?ids=${key}`, {
      tickets: [{ id: key, additional_tags: text }],
    });
    if (response.status !== 200) {
      throw new Error(ERROR_MESSAGES.EXECUTION_ERROR.description);
    }
    if (text.length === 1 && text.startsWith('')) {
      return returnOperationResult({
        status: RESPONSE_CODES.WARNING,
        data: [
          [
            key,
            {
              [ZendeskKey.TICKET_ID]: key,
            },
          ],
        ],
      });
    }
    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: [
        [
          key,
          {
            [ZendeskKey.TICKET_ID]: key,
          },
        ],
      ],
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

const getTicket = async (
  authToken: string,
  ticketId: string,
  domain: string,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.get(`/tickets/${ticketId}`);
    const ticket = response.data.ticket;
    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: [
        [
          ticket.id,
          {
            [ZendeskKey.TICKET_ID]: ticket.id,
            [ZendeskKey.TICKET_DESCRIPTION]: ticket.description,
            [ZendeskKey.TICKET_SUBJECT]: ticket.subject,
            [ZendeskKey.TICKET_URL]: ZENDESK_TICKET_URL(domain, ticketId),
          },
        ],
      ],
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

const getLatestTicket = async (
  authToken: string,
  domain: string,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.get('/tickets', {
      params: { sort_by: 'created_at', sort_order: 'desc', per_page: 1 },
    });

    if (!response.data.tickets || response.data.tickets.length === 0) {
      return returnOperationResult({
        status: RESPONSE_CODES.WARNING,
        data: getDefaultOperationReturn([
          ZendeskKey.TICKET_SUBJECT,
          ZendeskKey.TICKET_DESCRIPTION,
          ZendeskKey.TICKET_ID,
          ZendeskKey.TICKET_URL,
          ZendeskKey.TICKET_THREAD,
        ]),
        message: ERROR_MESSAGES.NO_ITEMS.description,
        errorCode: ERROR_MESSAGES.NO_ITEMS.errorCode,
      });
    }

    const latestTicket: Ticket = response.data.tickets[0];
    const threadHistory = await fetchTicketComments(zendeskApi, latestTicket.id);

    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: [
        [
          latestTicket.id,
          {
            [ZendeskKey.TICKET_ID]: latestTicket.id,
            [ZendeskKey.TICKET_DESCRIPTION]: latestTicket.description,
            [ZendeskKey.TICKET_SUBJECT]: latestTicket.subject,
            [ZendeskKey.TICKET_URL]: ZENDESK_TICKET_URL(domain, latestTicket.id),
            [ZendeskKey.TICKET_THREAD]: JSON.stringify(threadHistory),
          },
        ],
      ],
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

const fetchTicketComments = async (
  zendeskApi: ReturnType<typeof getAuthenticatedZendeskInstance>,
  ticketId: string,
): Promise<ZendeskTicketComment[]> => {
  const response = await zendeskApi.get(`/tickets/${ticketId}/comments`);
  return response.data.comments.map((comment: ZendeskTicketComment) => ({
    id: comment.id,
    body: comment.body,
    author_id: comment.author_id,
    created_at: comment.created_at,
  }));
};

const getTickets = async (
  authToken: string,
  domain: string,
  callSource: AiBlockRequestSource,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    if (callSource === AiBlockRequestSource.FlowTest || callSource === AiBlockRequestSource.InApp) {
      return getLatestTicket(authToken, domain);
    }
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.get('/tickets');
    const tickets = await Promise.all(
      response.data.tickets.map(async (ticket: Ticket) => {
        const threadHistory = await fetchTicketComments(zendeskApi, ticket.id);
        return [
          ticket.id,
          {
            [ZendeskKey.TICKET_ID]: ticket.id,
            [ZendeskKey.TICKET_DESCRIPTION]: ticket.description,
            [ZendeskKey.TICKET_SUBJECT]: ticket.subject,
            [ZendeskKey.TICKET_URL]: ZENDESK_TICKET_URL(domain, ticket.id),
            [ZendeskKey.TICKET_THREAD]: JSON.stringify(threadHistory),
          },
        ];
      }),
    );

    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: tickets,
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

const updateTicketStatus = async (
  authToken: string,
  domain: string,
  metadata: { statusId: string },
  [key]: NodeInputValues,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    const statusId = metadata?.statusId;
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.put(`/tickets/update_many.json?ids=${key}`, {
      tickets: [{ id: key, custom_status_id: statusId }],
    });
    if (response.status !== 200) {
      throw new Error(ERROR_MESSAGES.EXECUTION_ERROR.description);
    }
    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: [
        [
          key,
          {
            [ZendeskKey.TICKET_ID]: key,
          },
        ],
      ],
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

const assignTicket = async (
  authToken: string,
  domain: string,
  metadata: { userId: string },
  [key]: NodeInputValues,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    const userId = metadata?.userId;
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.put(`/tickets/update_many.json?ids=${key}`, {
      tickets: [{ id: key, assignee_id: userId }],
    });
    if (response.status !== 200) {
      throw new Error(ERROR_MESSAGES.EXECUTION_ERROR.description);
    }
    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: [
        [
          key,
          {
            [ZendeskKey.TICKET_ID]: key,
          },
        ],
      ],
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

const commentOnTicket = async (
  authToken: string,
  domain: string,
  metadata: { userId: string },
  [key, text]: NodeInputValues,
): Promise<OperationResultResponse | GenericResponse> => {
  try {
    const userId = metadata?.userId;
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.put(`/tickets/update_many.json?ids=${key}`, {
      tickets: [{ id: key, comment: { body: text[0]?.value, author_id: userId } }],
    });
    if (response.status !== 200) {
      throw new Error(ERROR_MESSAGES.EXECUTION_ERROR.description);
    }
    return returnOperationResult({
      status: RESPONSE_CODES.SUCCESS,
      data: [
        [
          key,
          {
            [ZendeskKey.TICKET_ID]: key,
          },
        ],
      ],
    });
  } catch (error) {
    return errorHandler(error as Error);
  }
};

export const getZendeskCustomTicketStatus = async (
  authToken: string,
  domain: string,
): Promise<ZendeskTicketStatus[] | void> => {
  try {
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.get('/custom_statuses');
    if (response.status !== 200) {
      throw new Error(ERROR_MESSAGES.EXECUTION_ERROR.description);
    }
    return response.data.custom_statuses;
  } catch (error) {
    errorHandler(error as Error);
  }
};

export const getZendeskAssignableUsers = async (authToken: string, domain: string): Promise<ZendeskUser[] | void> => {
  try {
    const zendeskApi = getAuthenticatedZendeskInstance(authToken, domain);
    const response = await zendeskApi.get('/group_memberships/assignable');
    if (response.status !== 200) {
      throw new Error(ERROR_MESSAGES.EXECUTION_ERROR.description);
    }
    const memberships = response.data.group_memberships;
    const membershipsIds = memberships.map((m: ZendeskMembership) => m.user_id);
    const res = await zendeskApi.get(`/users/show_many?ids=${membershipsIds}`);
    return res.data.users;
  } catch (error) {
    errorHandler(error as Error);
  }
};

const ZendeskProvider: CustomerServiceProvider = {
  getTicket,
  getTickets,
  updateTicketCategories,
};

export enum ZendeskKey {
  TICKET_ID = 'id',
  TICKET_SUBJECT = 'subject',
  TICKET_DESCRIPTION = 'description',
  TICKET_TAG = 'tag',
  TICKET_URL = 'url',
  TICKET_THREAD = 'ticketThread',
}

export const zendeskOutputKeys: Record<ZendeskKey, OperationInput> = {
  [ZendeskKey.TICKET_ID]: {
    code: ZendeskKey.TICKET_ID,
    name: 'Ticket id',
    default: false,
  },
  [ZendeskKey.TICKET_SUBJECT]: {
    code: ZendeskKey.TICKET_SUBJECT,
    name: 'Ticket subject',
    default: false,
  },
  [ZendeskKey.TICKET_DESCRIPTION]: {
    code: ZendeskKey.TICKET_DESCRIPTION,
    name: 'Ticket description',
    default: true,
  },
  [ZendeskKey.TICKET_TAG]: {
    code: ZendeskKey.TICKET_TAG,
    name: 'Ticket tag',
    default: false,
  },
  [ZendeskKey.TICKET_URL]: {
    code: ZendeskKey.TICKET_URL,
    name: 'Ticket url',
    default: false,
  },
  [ZendeskKey.TICKET_THREAD]: {
    code: ZendeskKey.TICKET_THREAD,
    name: 'Ticket thread',
    default: false,
  },
};

export const operations: Operation[] = [
  {
    ...zendeskOperationsDefinition.GetTickets,
    provider: IntegrationAuthApp.zendeskV1,
    type: AppNodeType.TRIGGER,
    expectedParams: [PARAMS.ZENDESK_AUTH_TOKEN, PARAMS.CS_DOMAIN, PARAMS.CALL_SOURCE],
    return: [PARAMS.OUTPUT],
    func: getTickets,
    outputKeys: [
      zendeskOutputKeys[ZendeskKey.TICKET_ID],
      zendeskOutputKeys[ZendeskKey.TICKET_SUBJECT],
      zendeskOutputKeys[ZendeskKey.TICKET_DESCRIPTION],
      zendeskOutputKeys[ZendeskKey.TICKET_URL],
      zendeskOutputKeys[ZendeskKey.TICKET_THREAD],
    ],
  },
  {
    ...zendeskOperationsDefinition.UpdateTicketTags,
    provider: IntegrationAuthApp.zendeskV1,
    type: AppNodeType.TARGET,
    expectedParams: [PARAMS.ZENDESK_AUTH_TOKEN, PARAMS.CS_DOMAIN, PARAMS.INPUT],
    return: [PARAMS.OUTPUT],
    func: updateTicketCategories,
    outputKeys: [zendeskOutputKeys[ZendeskKey.TICKET_ID]],
  },
  {
    ...zendeskOperationsDefinition.UpdateTicketStatus,
    provider: IntegrationAuthApp.zendeskV1,
    type: AppNodeType.TARGET,
    expectedParams: [PARAMS.ZENDESK_AUTH_TOKEN, PARAMS.CS_DOMAIN, PARAMS.METADATA, PARAMS.INPUT],
    return: [PARAMS.OUTPUT],
    func: updateTicketStatus,
    outputKeys: [zendeskOutputKeys[ZendeskKey.TICKET_ID]],
  },
  {
    ...zendeskOperationsDefinition.AssignTicket,
    provider: IntegrationAuthApp.zendeskV1,
    type: AppNodeType.TARGET,
    expectedParams: [PARAMS.ZENDESK_AUTH_TOKEN, PARAMS.CS_DOMAIN, PARAMS.METADATA, PARAMS.INPUT],
    return: [PARAMS.OUTPUT],
    func: assignTicket,
    outputKeys: [zendeskOutputKeys[ZendeskKey.TICKET_ID]],
  },
  {
    ...zendeskOperationsDefinition.CommentOnTicket,
    provider: IntegrationAuthApp.zendeskV1,
    type: AppNodeType.TARGET,
    expectedParams: [PARAMS.ZENDESK_AUTH_TOKEN, PARAMS.CS_DOMAIN, PARAMS.METADATA, PARAMS.INPUT],
    return: [PARAMS.OUTPUT],
    func: commentOnTicket,
    outputKeys: [zendeskOutputKeys[ZendeskKey.TICKET_ID]],
  },
];

export const ZendeskApp: FullApp = {
  id: IntegrationAuthApp.zendeskV1,
  title: 'Zendesk',
  icon: 'zendesk',
  source: AppSource.ZENDESK,
  provider: NangoIntegrationProvider.ZENDESK,
  triggers: operations.filter((op) => op.type === AppNodeType.TRIGGER || op.type === AppNodeType.BOTH),
  targets: operations.filter((op) => op.type === AppNodeType.TARGET || op.type === AppNodeType.BOTH),
  connectionName,
};

export default ZendeskProvider;
